import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  Layers3,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';

type PricingInterval = 'monthly' | 'yearly';
type PlanTier = 'PRO' | 'MAX' | 'ENTERPRISE';

type StaticPlan = {
  tier: PlanTier;
  name: string;
  description: string;
  price: string;
  priceSuffix: string;
  caption: string;
  credits: string;
  ctaLabel: string;
  featured?: boolean;
};

const SIGNUP_URL = 'https://radone.somatiq.ai/signup';
const CONTACT_URL = 'https://radone.somatiq.ai/contact-us';

const sharedPlanFeatures = [
  'Voice dictation',
  'Auto-structuring',
  'AI collaborator',
  'Differential generation',
  'Copy-to-any-RIS export',
  '7-day free trial',
];

const planIcons: Record<PlanTier, LucideIcon> = {
  PRO: Layers3,
  MAX: Sparkles,
  ENTERPRISE: Building2,
};

const plansByInterval: Record<PricingInterval, StaticPlan[]> = {
  monthly: [
    {
      tier: 'PRO',
      name: 'Pro',
      description: 'For individual radiologists building a faster reporting workflow.',
      price: '₹1,800',
      priceSuffix: '/ mo*',
      caption: 'Billed monthly',
      credits: '100 credits / day',
      ctaLabel: 'Get started',
    },
    {
      tier: 'MAX',
      name: 'Max',
      description: 'For high-volume reporting with more AI capacity every day.',
      price: '₹3,600',
      priceSuffix: '/ mo*',
      caption: 'Billed monthly',
      credits: '300 credits / day',
      ctaLabel: 'Get started',
      featured: true,
    },
    {
      tier: 'ENTERPRISE',
      name: 'Enterprise',
      description: 'For larger teams that need rollout support and custom commercials.',
      price: 'Custom',
      priceSuffix: '',
      caption: 'Volume and contract pricing',
      credits: 'Custom usage limits',
      ctaLabel: 'Talk to us',
    },
  ],
  yearly: [
    {
      tier: 'PRO',
      name: 'Pro',
      description: 'For individual radiologists building a faster reporting workflow.',
      price: '₹1,440',
      priceSuffix: '/ mo*',
      caption: '₹17,280 billed annually',
      credits: '100 credits / day',
      ctaLabel: 'Get started',
    },
    {
      tier: 'MAX',
      name: 'Max',
      description: 'For high-volume reporting with more AI capacity every day.',
      price: '₹2,880',
      priceSuffix: '/ mo*',
      caption: '₹34,560 billed annually',
      credits: '300 credits / day',
      ctaLabel: 'Get started',
      featured: true,
    },
    {
      tier: 'ENTERPRISE',
      name: 'Enterprise',
      description: 'For larger teams that need rollout support and custom commercials.',
      price: 'Custom',
      priceSuffix: '',
      caption: 'Volume and contract pricing',
      credits: 'Custom usage limits',
      ctaLabel: 'Talk to us',
    },
  ],
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

function getPlanButtonClasses(plan: StaticPlan) {
  if (plan.tier === 'ENTERPRISE') {
    return 'bg-transparent text-foreground border border-border px-4 py-2.5 sm:px-5 sm:py-3 rounded-md text-sm sm:text-[15px] font-normal cursor-pointer hover:bg-foreground/5 transition-colors';
  }

  return 'px-5 py-2.5 sm:px-7 sm:py-3 text-sm sm:text-[15px] bg-[#056EE7]/20 border border-[#056EE7]/40 font-semibold tracking-wider uppercase text-foreground rounded-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 overflow-hidden';
}

export function RadonePricingSection() {
  const [interval, setInterval] = useState<PricingInterval>('monthly');
  const plans = plansByInterval[interval];

  return (
    <motion.section
      className="py-24"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      variants={stagger}
    >
      <motion.div variants={fadeUp}>
        <span className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.08em] uppercase text-muted-foreground rounded-full px-4 py-1.5 mb-6 pl-0">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--brilliant-azure)]" />
          Pricing
          <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
        </span>
      </motion.div>

      <motion.div className="flex flex-col gap-6" variants={fadeUp}>
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-4">
            Simple pricing for
            <br />
            <span className="hero-wave-text">serious reporting.</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            No per-report fees. No integration tax. Just plans that fit solo
            radiologists, heavy reporting days, and larger teams.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="inline-flex w-fit self-start rounded-md border border-border bg-card p-1">
            <button
              type="button"
              onClick={() => setInterval('monthly')}
              className={cn(
                'rounded-md px-4 py-1.5 text-sm transition-colors cursor-pointer',
                interval === 'monthly'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-pressed={interval === 'monthly'}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setInterval('yearly')}
              className={cn(
                'rounded-md px-4 py-1.5 text-sm transition-colors cursor-pointer',
                interval === 'yearly'
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-pressed={interval === 'yearly'}
            >
              Annual
            </button>
          </div>

          <span className="inline-flex w-fit items-center rounded-full border border-[var(--brilliant-azure)]/20 bg-[var(--brilliant-azure)]/8 px-3 py-1 text-xs font-medium text-foreground">
            Save 20% annually
          </span>
        </div>
      </motion.div>

      <motion.div
        key={interval}
        className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3 mt-12"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        {plans.map((plan) => {
          const Icon = planIcons[plan.tier];

          return (
            <article
              key={`${interval}-${plan.tier}`}
              className={cn(
                'relative flex h-full flex-col items-center rounded-2xl border p-6 text-center md:p-8',
                plan.featured
                  ? 'border-[var(--brilliant-azure)]/40 bg-[linear-gradient(180deg,rgba(5,110,231,0.1),rgba(5,110,231,0.03))]'
                  : 'border-border bg-card'
              )}
            >
              {plan.featured && (
                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                  <span className="inline-flex rounded-full bg-[var(--brilliant-azure)] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-[0_10px_30px_rgba(5,110,231,0.28)]">
                    Most popular
                  </span>
                </div>
              )}

              <div className="mb-5 flex min-h-[6.5rem] flex-col items-center justify-start gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-foreground/5 text-[var(--brilliant-azure)]">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 space-y-1">
                  <h3 className="text-2xl font-medium">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>
              </div>

              <div className="mb-6 flex flex-col items-center">
                <div className="flex items-end justify-center gap-2">
                  <span className="font-display text-4xl md:text-5xl leading-none tracking-tight">
                    {plan.price}
                  </span>
                  {plan.priceSuffix && (
                    <span className="pb-1 text-sm text-muted-foreground">
                      {plan.priceSuffix}
                    </span>
                  )}
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  {plan.caption}
                  {plan.tier !== 'ENTERPRISE' && (
                    <span className="ml-1">• *GST extra</span>
                  )}
                </p>
              </div>

              {plan.tier === 'ENTERPRISE' ? (
                <a
                  href={CONTACT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-8 inline-flex w-fit justify-center rounded-lg border border-[var(--brilliant-azure)]/30 bg-[linear-gradient(180deg,rgba(5,110,231,0.12),rgba(5,110,231,0.05))] px-4 py-2 text-sm text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition-colors hover:bg-[linear-gradient(180deg,rgba(5,110,231,0.18),rgba(5,110,231,0.08))]"
                >
                  Contact us
                </a>
              ) : (
                <div className="mb-8 inline-flex w-fit justify-center rounded-full border border-white/[0.08] bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.04))] px-4 py-2 text-sm text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                  {plan.credits}
                </div>
              )}
              {/* {plan.tier === 'ENTERPRISE' ? (
                <a
                  href={CONTACT_URL}
                  className={cn(
                    'mt-auto inline-flex w-full items-center justify-center gap-2',
                    getPlanButtonClasses(plan)
                  )}
                >
                  {plan.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </a>
              ) : (
                <div className="relative mt-auto w-full">
                  <div className="absolute inset-0 bg-[#006AE5]/70 z-10 blur-2xl w-[70%] mx-auto" />
                  <a
                    href={SIGNUP_URL}
                    className={cn(
                      'relative z-20 inline-flex w-full items-center justify-center gap-2',
                      getPlanButtonClasses(plan)
                    )}
                  >
                    {plan.ctaLabel}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              )} */}
            </article>
          );
        })}
      </motion.div>



      <motion.div className="space-y-4 text-center mt-10" variants={fadeUp}>
        <p className="text-sm font-medium uppercase tracking-[0.24em] text-muted-foreground">
          All plans include
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {sharedPlanFeatures.map((feature) => (
            <div
              key={feature}
              className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-4 py-2 text-sm text-foreground"
            >
              <Check className="h-4 w-4 text-[var(--brilliant-azure)]" />
              {feature}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div className="mt-10 flex flex-col items-center gap-3" variants={fadeUp}>
        <div className="relative">
          <div className="absolute inset-0 bg-[#006AE5]/70 z-10 blur-2xl w-[70%] mx-auto" />
          <a
            href={SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'relative z-20 inline-flex items-center justify-center gap-2',
              getPlanButtonClasses(plans[0])
            )}
          >
            Get started for free
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <p className="text-sm text-muted-foreground">
          <span className="mr-1">*</span>
          No credit card required
        </p>
      </motion.div>
    </motion.section>
  );
}
